import util from 'util'
import Promise from 'bluebird'
import moment from 'moment'
import _ from 'lodash'

const stringify = JSON.stringify
const READ_INTERVAL = 30 // cycle counts (30 cycles per read ~30s)
const WRITE_INTERVAL = 5 // cycle counts (5 cycles per write ~5s)
/**
 * Attachments {
 * profile: user information (empty)
 * company: company information (filled)
 * uaid: unique attachment id (public)
 * url: url related to attachment (linkedin, etc)
 * name: name of attachment (unique in context/company context)
 * }
 */

const state = {
    server_data: [],
    local_add: [], // list of new attachment to add
    local_delete: [], // list of saved attachment to delete
    local_update: [], // list of saved attachment to modify
    has_data        : false,
    next_id         : 0,
    _write_timer    : 0,
    _force_fetch: false,
    _read_timer     : 0,
    _processing_cycle   : false // necessary to make sure we aren't doing multiple reads/writes at the same time (single thread for now)
}

const getters = {
    attachmentData (state) {
      // take our serverdata, remove any in local_delete, take all in update and add all in add
      let retval = _.map(state.server_data, data => {
        if (_.indexOf(state.local_delete, data._id) >= 0) return null
        let index = _.findIndex(state.local_update, function(u) {return u._id === data._id})
        if (index >= 0) return state.local_update[index]
        return data
      })
      retval = _.concat(retval, state.local_add)
      return _.without(retval, null, undefined)
    },
    hasData (state) {
        return state.has_data || state.server_data.length > 0
    },
    isDirty (state) {
        return state.local_add.length > 0 || state.local_delete.length > 0 || state.local_update.length > 0
    },
    nextId (state) {
      return (state.next_id++)
    }
}

const mutations = {
    initialize (state) {
        // revert the portfolio back to '0'
        state.server_data = []
        state.local_add = [] // list of new attachment to add
        state.local_delete = [] // list of saved attachment to delete
        state.local_update = [] // list of saved attachment to modify
        state.has_data = false
        state.next_id = 0
        state._write_timer = 0
        state._force_fetch = false
        state._read_timer = 0
        state._processing_cycle = false
    },
    markData (state, payload) {
      state.has_data = payload.hasData || state.server_data.length > 0
    },
    overwrite (state, payload) {
      // stomp on our current data, this will wipe out local changes
      state.server_data = payload
      // stomp local changes
      state.local_add = []
      state.local_delete = []
      state.local_update = [] 
    },
    clean_add (state, payload) {
      state.local_add = _.filter(state.local_add, function(a) {return (_.findIndex(payload, function(i) {return i.company == a.company && i.context == a.context && i.name == a.name}) < 0)})
      state.local_add = _.without(state.local_add, null, undefined)
    },
    clean_delete (state, payload) {
      state.local_delete = _.without(state.local_delete, payload)
      state.local_delete = _.without(state.local_delete, null, undefined)
    },
    clean_update (state, payload) {
      state.local_update = _.filter(state.local_update, function(u) {return (_.findIndex(payload, function(i) {return i._id === u._id}) < 0)})
      state.local_update = _.without(state.local_update, null, undefined)
    },
    merge (state, payload) {
      // general merge with our server_data
      state.server_data = _.map(state.server_data, item => {
        let index = _.findIndex(payload, function(p) {return p._id === item._id})
        if (index >= 0) return _.merge(item, _.first(_.pullAt(payload, index)))
        return item
      })
      if (!_.isEmpty(payload)) state.server_data = _.concat(state.server_data, payload)
      state.server_data = _.without(state.server_data, undefined, null)
    },
    merge_add (state, payload) {
      // merge all added
      if (!_.isArray(payload)) payload = [payload]
      state.server_data = _.concat(state.server_data, payload)
      state.server_data = _.without(state.server_data, null, undefined)
    },
    merge_delete (state, payload) {
      // remove all that were deleted
      if (!_.isArray(payload)) payload = [payload]
      state.server_data = _.map(state.server_data, data => {
        return (_.indexOf(payload, data.uaid) >= 0) ? null : data
      })
      state.server_data = _.without(state.server_data, null, undefined)
    },
    merge_update (state, payload) {
      // take all updated
      if (!_.isArray(payload)) payload = [payload]
      state.server_data = _.map(state.server_data, data => {
        if (_.isNil(data)) return data
        let index = _.findIndex(payload, function(u) {return u._id === data._id})
        if (index >= 0) return _.first(_.pullAt(payload, index))
        return data
      })
      state.server_data = _.without(state.server_data, null, undefined)
    },
    update_add (state, payload) {
      payload = _.map(payload, item => {
        item._dirty = moment()
        return item
      })
      state.local_add = _.map(state.local_add, cache => {
        let index = _.findIndex(payload, function(p) {return p.company === cache.company && p.context === cache.context && p.name === cache.name})
        if (index >= 0) return _.first(_.pullAt(payload, index))
        return cache
      })
      if (!_.isEmpty(payload)) state.local_add = _.concat(state.local_add, payload)
    },
    update_delete (state, payload) {
      // delete is an array of uaids (no objects)
      if (!_.isArray(payload)) payload = [payload]
      state.local_delete = _.union(state.local_delete, payload)
    },
    update_update (state, payload) {
      if (!_.isArray(payload)) payload = [payload]
      payload = _.map(payload, item => {
        if (_.isNil(item)) return null
        item._dirty = (moment())
        return item
      })
      payload = _.without(payload, null, undefined)
      state.local_update = _.map(state.local_update, cache => {
        let index = _.findIndex(payload, function (p) {return p._id === cache._id})
        if (index >= 0) return _.first(_.pullAt(payload, index))
        return cache
      })
      if (!_.isEmpty(payload)) state.local_update = _.concat(state.local_update, payload)
    }
}

const actions = {
  start () {},
  stop ({commit}) {
    commit('initialize')
  },
  cycle () {},
  clean ({commit}, payload) {
    // run each of our cleans
    commit('clean_add', payload.add||[])
    commit('clean_delete', payload.delete||[])
    commit('clean_update', payload.update||[])
  },
  merge ({commit}, payload) {
    // merge should have: delete, add, update
    if (!_.isNil(payload.add)) {
      commit('merge_add', payload.add)
    }
    if (!_.isNil(payload.delete)) {
      commit('merge_delete', payload.deleted)
    }
    if (!_.isNil(payload.update)) {
      commit('merge_udpate', payload.update)
    }
  },
  update ({commit}, payload) {
    // update our 3 caches
    if (!_.isEmpty(payload.add)) {
      commit('update_add', payload.add)
    }
    if (!_.isEmpty(payload.delete)) {
      commit('update_delete', payload.delete)
    }
    if (!_.isEmpty(payload.update)) {
      commit('update_update', payload.update)
    }
  },
  forceRead ({state}) {
      state._read_timer = READ_INTERVAL
  },
  forceWrite ({state}) {
      state._write_timer = WRITE_INTERVAL
  },
  apiPostAttachment ({commit,dispatch}, payload) {
    return Promise.resolve({})
  },
  apiPatchAttachment ({state,commit}) {
    return Promise.resolve({})
  },
  apiFetchAttachment ({state,commit}) {
    return Promise.resolve({})
  },
  apiCleanFetchAttachment ({commit}) {
    // full fetch from the server, overwriting local infos
    return Promise.resolve({})
  }
}

export default {
    namespaced : true,
    state,
    getters,
    mutations,
    actions
}